import React, { PureComponent } from 'react';
import CommonMap from './CommonMap';
import GridLayer, { GridLayerFeature } from './layers/GridLayer';
import CorePlacementGridLayer from './layers/CorePlacementGridLayer';
import { MAP_TYPES } from './helpers/mapTypes';
import { Point, Polygon } from 'ol/geom';
import { OnLoading, SettingsAccessLevel } from '../../types/types';
import LayerCheckContainer from './buttons/LayerCheckContainer';
import LayerCustomCheckBox from './buttons/LayerCustomCheck';
import MapMakerScheduleLayer from './layers/MapMakerScheduleLayer';
import { GeometryObject, Feature as TurfFeature, Feature as GeoJSONFeature } from 'geojson';
import { getCurrentMission } from '../../dataModelHelpers';
import EventBus from '../../EventBus';
import { MISSION_EVENTS } from '../../missionEvents';
import Feature from 'ol/Feature';
import { LocalAtRecord, ZoneType } from '../../db';
import { getArea } from 'ol/sphere';
import { convertProjection4329, normalizeDoubleEncodedJsonString, round, roundToIncrement } from '../../utils';
import { Button, Typography } from '@mui/material';
import { KM_PER_MILE, SQ_M_PER_ACRE } from '../../constants';
import ScheduleFieldZoomButton from './buttons/ScheduleFieldZoomButton';
import { dispatchCoresNeedUpdate, MAP_EVENTS } from '../../mapEvents';
import { MapDebugStorage, MapDebugZoomLevel, MapShowPulledCoreLocation, MapZoomStorage } from '../../db/local_storage';
import MapButton from './buttons/MapButton';
import { GeoData } from '@rogoag/shared-ts';
import { Buffer } from 'buffer';
import { GenericPointsLayer } from './layers/GenericPointsLayer';
import { DefectiveGeometriesLayer } from './layers/DefectiveGeometriesLayer';
import RoutingBoundaryLayer from './layers/RoutingBoundaryLayer';
import RobotPathLayer, {
  ROBOT_PATH_DEFAULT_VISIBLE,
  ROBOT_PATH_REFRESH_DEBOUNCE_MS,
  robotPathCaption,
} from './layers/RobotPathLayer';
import DebugGeoJsonLayer from './layers/DebugGeoJsonLayer';
import { DubinsWheelType } from '../../db/local_storage';
import { simulateRobotPath } from '../../services/paths/robotPath/RobotPathClient';
import { resolveTurnRadius, WheelType } from '../../services/paths/robotPath/turnRadius';
import type { RobotPathResult } from '../../services/paths/robotPath/types';
import { bridgesForOverlay, type SmartPathBridge } from '../../services/paths/bridges';
import { feature } from 'react-openlayers';
import { alertSuccess, alertWarn } from '../../alertDispatcher';
import { TbAnalyzeFilled } from 'react-icons/tb';
import { Coordinate } from 'ol/coordinate';
import { HistoricalLayersService } from '../../types/historical-layers';
import PathArrowsLayer from './layers/PathArrowsLayer';
import { formatMapAcres, MapAcresSummary } from '../schedule/helpers/mapAcres';

interface MapMakerMapProps {
  accessLevel: SettingsAccessLevel;
  missionActive: string;
  scheduleFeatures: TurfFeature<GeometryObject>[];
  /** Finalized maps, in shift order, still ahead of the operator in the loaded mission's shift. */
  shiftMapAcres?: MapAcresSummary;
  loadMissionMaps: (jobId: string) => Promise<void>;
  onLoading: OnLoading;
  openExitInterview: () => void;
  historicalLayersService?: HistoricalLayersService;
}

interface MapMakerMapState {
  gridLayerFeatures: GridLayerFeature[];
  showOrderLine: boolean;
  showFieldOrder: boolean;
  showScheduledJobs: boolean;
  fields: Feature<Polygon>[];
  density: number;
  acres: number;
  uploadedFeatures: Feature<Point>[];
  showPulledCoreLocation: boolean;
  defectiveGeometries: GeoJSONFeature<GeoJSON.Point | GeoJSON.Polygon>[];
  warningGeometries: GeoJSONFeature<GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString>[];
  showDefectiveGeometries: boolean;
  showTimeline: boolean;
  showSafeBoundary: boolean;
  showRobotPath: boolean;
  robotPath: RobotPathResult | null;
  /** The bridges of the boundary the last planning run used (Robot path overlay). */
  robotPathBridges: SmartPathBridge[];
  showDebugGeoJson: boolean;
  showDubinsTspCost: boolean;
  showDgpCurves: boolean;
  showDirectDubins: boolean;
  showHybridAstarCurves: boolean;
}

// Inline colour swatch used in debug-overlay toggle labels so the user
// associates a checkbox with the colour the corresponding feature renders
// in on the map. Sized to read alongside Typography variant="caption".
const ColorSwatch = ({ color }: { color: string }) => (
  <span
    style={{
      display: 'inline-block',
      width: 10,
      height: 10,
      backgroundColor: color,
      border: '1px solid rgba(0,0,0,0.4)',
      verticalAlign: 'middle',
      marginLeft: 2,
    }}
  />
);

export default class MapMakerMap extends PureComponent<MapMakerMapProps, MapMakerMapState> {
  constructor(props: MapMakerMapProps) {
    super(props);

    this.state = {
      gridLayerFeatures: [],
      showOrderLine: false,
      showFieldOrder: true,
      showScheduledJobs: true,
      fields: [],
      density: 0,
      acres: 0,
      uploadedFeatures: [],
      showPulledCoreLocation: MapShowPulledCoreLocation.get(),
      defectiveGeometries: [],
      warningGeometries: [],
      showDefectiveGeometries: true,
      showTimeline: false,
      showSafeBoundary: true,
      // Off until asked for: the overlay costs a /path/simulate round trip.
      showRobotPath: ROBOT_PATH_DEFAULT_VISIBLE,
      robotPath: null,
      robotPathBridges: [],
      showDebugGeoJson: true,
      showDubinsTspCost: false,
      showDgpCurves: false,
      showDirectDubins: false,
      showHybridAstarCurves: false,
    };
  }

  async componentDidMount() {
    EventBus.on(MISSION_EVENTS.UPDATED, this.initFields);
    EventBus.on(MISSION_EVENTS.BOUNDARY_UPDATED, this.validateMission);
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.validateMission);
    EventBus.on(MAP_EVENTS.PATH_UPDATED, this.validateMission);
    // The robot path goes stale on the same edits the path itself follows.
    EventBus.on(MAP_EVENTS.CORES_NEED_UPDATE, this.refreshRobotPathIfVisible);
    EventBus.on(MAP_EVENTS.PATH_UPDATED, this.refreshRobotPathIfVisible);
    EventBus.on(MISSION_EVENTS.DEFECTIVE_GEOMETRIES_UPDATED, this.onDefectiveGeometriesUpdated);

    await this.initFields();
    this.validateMission();
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.UPDATED, this.initFields);
    EventBus.remove(MISSION_EVENTS.BOUNDARY_UPDATED, this.validateMission);
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.validateMission);
    EventBus.remove(MAP_EVENTS.PATH_UPDATED, this.validateMission);
    EventBus.remove(MAP_EVENTS.CORES_NEED_UPDATE, this.refreshRobotPathIfVisible);
    EventBus.remove(MAP_EVENTS.PATH_UPDATED, this.refreshRobotPathIfVisible);
    EventBus.remove(MISSION_EVENTS.DEFECTIVE_GEOMETRIES_UPDATED, this.onDefectiveGeometriesUpdated);

    if (this.#robotPathRefreshTimer) {
      clearTimeout(this.#robotPathRefreshTimer);
      this.#robotPathRefreshTimer = undefined;
    }

    this.#robotPathFetchId++; // nothing in flight may land on an unmounted map
  }

  initFields = async () => {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    const fieldZone = mission.getZones().find((sel) => sel.zone_type === ZoneType.FIELD);

    if (!fieldZone) {
      return;
    }

    const features = await fieldZone.to_feature();
    const fields = features.map((feature) => {
      const coords = feature.geometry.coordinates.map((coords) =>
        coords.map((ring) => {
          return ring;
        }),
      );
      const geom = new Polygon(coords);

      return new Feature(geom);
    });
    await this.getDensity();
    this.setState({ fields });
  };

  async getDensity() {
    const currentMission = getCurrentMission();

    try {
      if (!currentMission?.job_id) {
        console.warn('No job_id set for current mission');

        return;
      }

      const record = LocalAtRecord.getRecord('Jobs', currentMission.job_id);

      if (record && record.get('Deal Density')) {
        this.setState({ density: record.get('Deal Density') as number });
      } else {
        console.warn('No Deal Density set for job');
      }
    } catch (err) {
      console.error('Could not pull airtable record for deal density', err);
    }
  }

  onDefectiveGeometriesUpdated = (payload: {
    defectiveGeometries: GeoJSONFeature<GeoJSON.Point | GeoJSON.Polygon>[];
    warningGeometries: GeoJSONFeature<GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString>[];
  }) => {
    this.setState({
      defectiveGeometries: payload.defectiveGeometries,
      warningGeometries: payload.warningGeometries,
    });
  };

  // /path/simulate is a network round trip, so the overlay fetches on demand
  // and re-fetches at most every ROBOT_PATH_REFRESH_DEBOUNCE_MS while it is on.
  #robotPathRefreshTimer?: ReturnType<typeof setTimeout>;

  // Only the newest fetch may land: a slower earlier one must not overwrite a
  // newer result, and nothing may land after unmount.
  #robotPathFetchId = 0;

  toggleRobotPath = () => {
    const showRobotPath = !this.state.showRobotPath;

    this.setState({ showRobotPath, robotPath: showRobotPath ? this.state.robotPath : null });

    if (showRobotPath) {
      this.fetchRobotPath();
    }
  };

  refreshRobotPathIfVisible = () => {
    if (!this.state.showRobotPath) {
      return;
    }

    if (this.#robotPathRefreshTimer) {
      clearTimeout(this.#robotPathRefreshTimer);
    }

    this.#robotPathRefreshTimer = setTimeout(() => {
      this.#robotPathRefreshTimer = undefined;
      this.fetchRobotPath();
    }, ROBOT_PATH_REFRESH_DEBOUNCE_MS);
  };

  fetchRobotPath = async () => {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    const fetchId = ++this.#robotPathFetchId;

    try {
      const result = await simulateRobotPath(mission, resolveTurnRadius(DubinsWheelType.get() as WheelType));

      if (fetchId === this.#robotPathFetchId) {
        this.setState({
          robotPath: result,
          robotPathBridges: bridgesForOverlay(mission.instance_id, mission.getPullinZones()),
        });
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Unknown error';

      console.warn('MapMakerMap - robot path failed', error);

      if (fetchId === this.#robotPathFetchId) {
        alertWarn(`Robot path failed: ${detail}`);
        this.setState({ robotPath: null, robotPathBridges: [] });
      }
    }
  };

  validateMission = () => {
    const currentMission = getCurrentMission();

    if (!currentMission) {
      this.setState({ defectiveGeometries: [], warningGeometries: [] });

      return;
    }

    const validationResult = currentMission.validateBeforeMapMakingSubmission();
    this.setState({
      defectiveGeometries: validationResult.defectiveGeometries || [],
      warningGeometries: validationResult.warningGeometries || [],
    });
  };

  getAcres() {
    let acres = 0.0;

    for (const f of this.state.fields) {
      const geom = f.getGeometry();

      if (geom) {
        acres += getArea(geom) / SQ_M_PER_ACRE; // m^2 to acres
      }
    }

    return round(acres, 2);
  }

  render() {
    const currentMission = getCurrentMission();
    const isZoneInterleaveMission = currentMission?.is_zone_interleave_mission();
    const backupSamples = JSON.parse(normalizeDoubleEncodedJsonString(currentMission?.backup_samples)) || [];
    const sitesType = currentMission?.getJob()?.sites_type;
    const isGrid = sitesType?.toLowerCase() !== 'zone';
    const targetSamples = this.state.density ? Math.ceil(this.getAcres() / this.state.density) : 0;
    const actualSamples = currentMission?.getSampleSites().length;
    const missionName = currentMission?.name;
    const jobId = currentMission?.job_id;

    return (
      <CommonMap
        type={MAP_TYPES.MAPS}
        statusItems={[
          Boolean(this.props.missionActive) && !!missionName && (
            <Typography
              key="mission-name-status"
              variant="body2"
              noWrap
              title={`${missionName} (click to copy)`}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(missionName);
                  alertSuccess(`Mission name copied: ${missionName}`);
                } catch (error) {
                  console.error('Failed to copy mission name:', error);
                }
              }}
              style={{
                maxWidth: 480,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: 12,
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                color: '#1565c0',
                cursor: 'pointer',
              }}
            >
              {missionName}
            </Typography>
          ),
          Boolean(this.props.missionActive) && !!jobId && (
            <Typography
              key="job-id-status"
              variant="body2"
              noWrap
              title={`${jobId} (click to copy)`}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(jobId);
                  alertSuccess(`Job ID copied: ${jobId}`);
                } catch (error) {
                  console.error('Failed to copy job ID:', error);
                }
              }}
              style={{
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'rgba(0,0,0,0.6)',
                cursor: 'pointer',
              }}
            >
              {jobId}
            </Typography>
          ),
          Boolean(this.props.missionActive) && !!sitesType && (
            <Typography key="sites-type-status" variant="body2" noWrap>
              {sitesType + `${isZoneInterleaveMission ? ' + inl' : ''}`}
            </Typography>
          ),
          Boolean(this.props.missionActive) && isGrid && (
            <Typography key="target-samples-status" variant="body2" noWrap>
              {`Target: ${targetSamples}`}
            </Typography>
          ),
          Boolean(this.props.missionActive) && isGrid && (
            <Typography key="actual-samples-status" variant="body2" noWrap>
              {`Actual: ${actualSamples}`}
            </Typography>
          ),
          !!this.props.shiftMapAcres?.totalJobs && (
            <Typography
              key="shift-map-acres-status"
              variant="body2"
              noWrap
              title={`First ${this.props.shiftMapAcres.mappedJobs} of ${this.props.shiftMapAcres.totalJobs} jobs in shift order have finalized maps`}
            >
              {`Shift: ${formatMapAcres(this.props.shiftMapAcres)}`}
            </Typography>
          ),
          Boolean(backupSamples.length) && (
            <Typography key="backup-samples-status" variant="body2" noWrap>
              {`${backupSamples.length} Backup Samples`}
            </Typography>
          ),
        ]}
        primaryActions={[
          Boolean(this.props.missionActive) && (
            <Button
              key="closeMissionButton"
              onClick={this.props.openExitInterview}
              size="small"
              variant="text"
              style={{ minHeight: 24, padding: '2px 10px', lineHeight: 1.2, whiteSpace: 'nowrap' }}
            >
              Close Mission
            </Button>
          ),
        ]}
        buttons={[
          <ScheduleFieldZoomButton
            key={'ScheduleFieldZoomButton'}
            updateCenter={(center) => {
              EventBus.dispatch(MAP_EVENTS.SET_CENTER, center.value);
            }}
            updateFit={(fit) => {
              EventBus.dispatch(MAP_EVENTS.SET_FIT, fit.value);
            }}
            // @ts-ignore
            scheduleFeatures={this.props.scheduleFeatures}
          />,

          <MapButton
            key={'ShowPulledCoreLocation'}
            onClick={() =>
              this.setState((prevState) => {
                const newValue = !prevState.showPulledCoreLocation;
                MapShowPulledCoreLocation.set(newValue);
                dispatchCoresNeedUpdate();

                return { showPulledCoreLocation: newValue };
              })
            }
            tooltip={`${this.state.showPulledCoreLocation ? 'Disable' : 'Enable'} showing pulled core location`}
            toggled={this.state.showPulledCoreLocation}
          >
            <TbAnalyzeFilled size={22} />
          </MapButton>,
          <LayerCheckContainer key="layerCheckContainerButton" customClassName="map-maker-layer-check">
            <LayerCustomCheckBox
              label={'Order Line'}
              onChange={(e) => {
                this.setState({ showOrderLine: e.target.checked });
              }}
              checked={this.state.showOrderLine}
            />
            <LayerCustomCheckBox
              label={'Field Order'}
              onChange={(e) => {
                this.setState({ showFieldOrder: e.target.checked });
              }}
              checked={this.state.showFieldOrder}
            />
            <LayerCustomCheckBox
              label={'Scheduled Jobs'}
              checked={this.state.showScheduledJobs}
              onChange={(e) => {
                this.setState({ showScheduledJobs: e.target.checked });
              }}
            />
            {Boolean(this.props.missionActive) &&
              (this.state.defectiveGeometries.length > 0 || this.state.warningGeometries.length > 0) && (
                <LayerCustomCheckBox
                  label={`Show Geometry Errors (${this.state.defectiveGeometries.length + this.state.warningGeometries.length})`}
                  checked={this.state.showDefectiveGeometries}
                  onChange={(e) => {
                    this.setState({ showDefectiveGeometries: e.target.checked });
                  }}
                />
              )}
            <LayerCustomCheckBox
              label={'Show Timeline'}
              checked={this.state.showTimeline}
              onChange={(e) => {
                this.setState({ showTimeline: e.target.checked });
              }}
            />
            <Button
              onClick={this.toggleRobotPath}
              size="small"
              variant={this.state.showRobotPath ? 'contained' : 'outlined'}
              sx={{ minHeight: 24, margin: '4px 0 4px 1', padding: '2px 10px', lineHeight: 1.2 }}
            >
              Robot path
            </Button>
            <LayerCustomCheckBox
              label={'Show Debug GeoJSON'}
              onChange={(e) => {
                this.setState({ showDebugGeoJson: e.target.checked });
              }}
              checked={this.state.showDebugGeoJson}
            />
            {this.props.accessLevel === 'Technician' && (
              <LayerCustomCheckBox
                label={
                  <>
                    Show TSP-cost Dubin&apos;s (debug, <ColorSwatch color="#FF1493" />)
                  </>
                }
                onChange={(e) => {
                  this.setState({ showDubinsTspCost: e.target.checked });
                }}
                checked={this.state.showDubinsTspCost}
              />
            )}
            {this.props.accessLevel === 'Technician' && (
              <LayerCustomCheckBox
                label={
                  <>
                    Show DGP curves (debug, <ColorSwatch color="#00CED1" />)
                  </>
                }
                onChange={(e) => {
                  this.setState({ showDgpCurves: e.target.checked });
                }}
                checked={this.state.showDgpCurves}
              />
            )}
            {this.props.accessLevel === 'Technician' && (
              <LayerCustomCheckBox
                label={
                  <>
                    Show Direct Dubin&apos;s (debug, <ColorSwatch color="#FFA500" />)
                  </>
                }
                onChange={(e) => {
                  this.setState({ showDirectDubins: e.target.checked });
                }}
                checked={this.state.showDirectDubins}
              />
            )}
            {this.props.accessLevel === 'Technician' && (
              <LayerCustomCheckBox
                label={
                  <>
                    Show Hybrid A* curves (debug, <ColorSwatch color="#bf94e4" />)
                  </>
                }
                onChange={(e) => {
                  this.setState({ showHybridAstarCurves: e.target.checked });
                }}
                checked={this.state.showHybridAstarCurves}
              />
            )}
            <LayerCustomCheckBox
              label={'Show Safe Boundary'}
              onChange={(e) => {
                this.setState({ showSafeBoundary: e.target.checked });
              }}
              checked={this.state.showSafeBoundary}
            />
            {Boolean(this.props.missionActive) && !!currentMission?.path_distance && (
              <Typography style={{ marginLeft: 10, display: 'block', marginTop: 8 }} variant={'caption'}>
                {`Current path: ${Math.round(currentMission.path_distance / 100 / KM_PER_MILE) / 10} mi`}
              </Typography>
            )}
            {Boolean(this.props.missionActive) && this.state.showRobotPath && this.state.robotPath && (
              <Typography style={{ marginLeft: 10, display: 'block' }} variant={'caption'}>
                {robotPathCaption(this.state.robotPath)}
              </Typography>
            )}
            {Boolean(this.props.missionActive) && (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
                <Typography style={{ marginLeft: 10 }} variant={'caption'}>
                  {'Pull-in: '}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${getCurrentMission()?.pullin_lat.toFixed(
                      6,
                    )}%2C${getCurrentMission()?.pullin_lon.toFixed(6)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {`${getCurrentMission()?.pullin_lat.toFixed(6)},${getCurrentMission()?.pullin_lon.toFixed(6)}`}
                  </a>
                </Typography>
              </div>
            )}

            {Boolean(this.props.missionActive) && (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography style={{ marginLeft: 10 }} variant={'caption'}>
                  <a
                    href={`https://airtable.com/appCnwyyyXRBezmvB/tblXij9xpNwUCLRIM/${getCurrentMission()?.job_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {'Open on AirTable'}
                  </a>
                </Typography>
              </div>
            )}

            {MapDebugStorage.get() && MapDebugZoomLevel.get() && (
              <Typography style={{ marginLeft: 10 }} variant={'caption'}>
                {`Map Zoom Level: ${roundToIncrement(MapZoomStorage.get().value, 0.1)}`}
              </Typography>
            )}
          </LayerCheckContainer>,
        ]}
        layers={[
          Boolean(this.props.missionActive) && (
            <GridLayer key="gridLayer" gridLayerFeatures={this.state.gridLayerFeatures} />
          ),
          Boolean(this.props.missionActive) && <CorePlacementGridLayer key="corePlacementGridLayer" />,
          <MapMakerScheduleLayer
            key="scheduleLayer"
            scheduleFeatures={this.props.scheduleFeatures}
            loadMissionMaps={this.props.loadMissionMaps}
            showOrderLine={this.state.showOrderLine}
            showFieldOrder={this.state.showFieldOrder}
            showScheduledJobs={this.state.showScheduledJobs}
            missionActive={this.props.missionActive}
          />,
          <GenericPointsLayer
            key="genericPointsLayer"
            points={this.state.uploadedFeatures.map((f, idx) => {
              const coordinate: Coordinate = convertProjection4329([
                // @ts-ignore
                f.geometry.coordinates[0],
                // @ts-ignore
                f.geometry.coordinates[1],
              ]);

              return (
                <feature.PointReact
                  coordinate={coordinate}
                  circleOptions={{
                    radius: 6,
                    fillOptions: { color: 'red' },
                    strokeOptions: { width: 1, color: 'white' },
                  }}
                  key={`${idx}_${coordinate[0]}_${coordinate[1]}`}
                  zIndex={100}
                />
              );
            })}
          />,
          Boolean(this.props.missionActive) && (
            <RoutingBoundaryLayer key="routing-boundary" visible={this.state.showSafeBoundary} />
          ),
          Boolean(this.props.missionActive) && <PathArrowsLayer key={'PathArrowsLayer'} />,
          Boolean(this.props.missionActive) && (
            <DefectiveGeometriesLayer
              key="defective-geometries"
              defectiveGeometries={this.state.showDefectiveGeometries ? this.state.defectiveGeometries : []}
              warningGeometries={this.state.showDefectiveGeometries ? this.state.warningGeometries : []}
            />
          ),
          Boolean(this.props.missionActive) && (
            <RobotPathLayer
              key="robot-path"
              visible={this.state.showRobotPath}
              result={this.state.robotPath}
              bridges={this.state.robotPathBridges}
            />
          ),
          Boolean(this.props.missionActive) && (
            <DebugGeoJsonLayer
              key="debug-geojson"
              visible={this.state.showDebugGeoJson}
              showDubinsTspCost={this.state.showDubinsTspCost}
              showDgpCurves={this.state.showDgpCurves}
              showDirectDubins={this.state.showDirectDubins}
              showHybridAstarCurves={this.state.showHybridAstarCurves}
            />
          ),
        ]}
        accessLevel={this.props.accessLevel}
        onLoading={this.props.onLoading}
        missionActive={this.props.missionActive}
        showTimeline={this.state.showTimeline}
        historicalLayersService={this.props.historicalLayersService}
      />
    );
  }
}
